<script setup lang="ts">
    import { computed, watch } from 'vue'

    const props = defineProps({
        password: {
            type: String,
            required: true
        }
    })

    const emit = defineEmits<{
        (e: 'update:strength', value: number): void
    }>()

    const PasswordStrength = {
        VERY_WEAK: 0,
        WEAK: 1,
        MEDIUM: 2,
        STRONG: 3,
        VERY_STRONG: 4
    }

    function estimateStrength(password: string) {
        if (!password.length) {
            return PasswordStrength.VERY_WEAK
        }

        const chars = Array.from(password)
        const length = chars.length
        const uniqueChars = new Set(chars).size

        let hasLower = false
        let hasUpper = false
        let hasDigit = false
        let hasSymbol = false
        let hasOther = false

        for (const ch of chars) {
            if (/[a-z]/.test(ch)) hasLower = true
            else if (/[A-Z]/.test(ch)) hasUpper = true
            else if (/[0-9]/.test(ch)) hasDigit = true
            else if (/[\p{P}\p{S}]/u.test(ch)) hasSymbol = true
            else hasOther = true
        }

        let pool = 0
        if (hasLower) pool += 26
        if (hasUpper) pool += 26
        if (hasDigit) pool += 10
        if (hasSymbol) pool += 33
        if (hasOther) pool += 100

        const entropy =
            uniqueChars * Math.log2(pool) + (length - uniqueChars) * Math.log2(uniqueChars)

        if (entropy >= 120) return PasswordStrength.VERY_STRONG
        if (entropy >= 100) return PasswordStrength.STRONG
        if (entropy >= 80) return PasswordStrength.MEDIUM
        if (entropy >= 60) return PasswordStrength.WEAK
        return PasswordStrength.VERY_WEAK
    }

    const strength = computed(() => estimateStrength(props.password))
    const progressValue = computed(() => (strength.value / PasswordStrength.VERY_STRONG) * 100)
    const strengthLabel = computed(() => {
        switch (strength.value) {
            case PasswordStrength.VERY_STRONG:
                return { label: 'Sehr stark', color: 'green-darken-2' }
            case PasswordStrength.STRONG:
                return { label: 'Stark', color: 'green' }
            case PasswordStrength.MEDIUM:
                return { label: 'Mittel', color: 'yellow-darken-2' }
            case PasswordStrength.WEAK:
                return { label: 'Schwach', color: 'orange' }
            default:
                return { label: 'Sehr schwach', color: 'red' }
        }
    })

    watch(strength, (value) => emit('update:strength', value), { immediate: true })
</script>

<template>
    <div>
        <v-progress-linear
            v-model="progressValue"
            chunk-count="4"
            chunk-gap="9"
            height="15"
            rounded="sm"
            :color="strengthLabel.color"
            bg-color="grey lighten-4"
        />

        <div class="mt-1 text-caption text-medium-emphasis">
            Passwortstärke:
            <strong>{{ strengthLabel.label }}</strong>
        </div>
    </div>
</template>
